import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, ArrowLeft, Bell, Play, Pencil, Archive as ArchiveIcon, ArchiveRestore, Shield, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD } from "@/lib/dates";
import { TIERS } from "@/lib/pricing";
import OverviewTab from "@/pages/debtors/profile/OverviewTab";
import DocumentsTab from "@/pages/debtors/profile/DocumentsTab";
import NotesTab from "@/pages/debtors/profile/NotesTab";
import QueryHistoryTab from "@/pages/debtors/profile/QueryHistoryTab";
import ReportsTab from "@/pages/debtors/profile/ReportsTab";
import RenewalTab from "@/pages/debtors/profile/RenewalTab";
import RunQueryModal from "@/components/queries/RunQueryModal";
import EditDebtorProfileModal from "@/components/debtors/EditDebtorProfileModal";
import CollectionEventsSection from "@/components/debtors/CollectionEventsSection";
import RecipientsPopover from "@/components/debtors/RecipientsPopover";
import TagPill from "@/components/tags/TagPill";
import TagAssignPopover from "@/components/tags/TagAssignPopover";
import { useTags } from "@/context/TagsContext";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { resolveTags } from "@/lib/tags";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "overview",       label: "Overview" },
  { key: "query-history",  label: "Query History" },
  { key: "reports",        label: "Reports" },
  { key: "renewal",        label: "Renewal" },
  { key: "documents",      label: "Documents" },
  { key: "notes",          label: "Notes" },
];

const StatusPill = ({ status }) => (
  <span
    className={cn(
      "inline-flex px-2 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]",
      status === "active" && "bg-success/15 text-success border border-success/40",
      status === "attention_required" && "bg-warning/15 text-warning border border-warning/40",
      status === "archived" && "bg-danger/15 text-danger border border-danger/40"
    )}
    data-testid="debtor-status-pill"
  >
    {status.replace("_", " ").toUpperCase()}
  </span>
);

const tierBadgeStyle = (tier) => {
  if (tier === "gold") return { background: "var(--color-tier-gold-bg-15)", border: "1px solid var(--color-tier-gold-border-40)", color: "var(--color-tier-gold)" };
  if (tier === "silver") return { background: "rgba(192, 192, 192, 0.15)", border: "1px solid rgba(192, 192, 192, 0.40)", color: "var(--color-tier-silver)" };
  return { background: "rgba(205, 127, 50, 0.15)", border: "1px solid rgba(205, 127, 50, 0.40)", color: "var(--color-tier-bronze)" };
};

const TierPill = ({ tier }) => {
  const info = TIERS.find((t) => t.key === tier) || TIERS[0];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]"
      style={tierBadgeStyle(tier)}
      data-testid="debtor-tier-pill"
    >
      <Shield size={12} strokeWidth={2.5} />
      {info.label}
    </span>
  );
};

const DebtorProfilePage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lookup, isPro } = useTags();
  const { canRunQueries, canMutateDebtors, canManageTeam } = useRolePermissions();
  const [debtor, setDebtor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [runQueryOpen, setRunQueryOpen] = useState(false);
  const [tagPopover, setTagPopover] = useState({ open: false, rect: null });
  const [recipientsPopover, setRecipientsPopover] = useState({ open: false, rect: null });
  const recipientsBtnRef = useRef(null);

  const activeTab = searchParams.get("tab") || "overview";
  const setTab = (t) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", t);
    setSearchParams(sp, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/debtors/${id}`);
      setDebtor(data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/debtors", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Optional deep-link: ?edit=profile opens the edit modal (mutators only).
  useEffect(() => {
    if (searchParams.get("edit") === "profile" && debtor && canMutateDebtors) setEditOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debtor, searchParams.get("edit"), canMutateDebtors]);

  const closeEdit = () => {
    setEditOpen(false);
    const sp = new URLSearchParams(searchParams);
    sp.delete("edit");
    setSearchParams(sp, { replace: true });
  };

  const onEditSaved = () => {
    closeEdit();
    load();
  };

  const onRunCompleted = (result) => {
    setRunQueryOpen(false);
    if (result?.query_run?.id) {
      nav(`/debtors/${id}/queries/${result.query_run.id}`);
    } else {
      load();
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${debtor.full_name}?`)) return;
    try {
      await apiClient.delete(`/debtors/${id}`);
      toast.success("Archived.");
      nav("/debtors");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restore = async () => {
    try {
      await apiClient.patch(`/debtors/${id}`, { status: "active" });
      toast.success("Debtor restored.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openTagPopover = (evt) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    setTagPopover({ open: true, rect });
  };
  const closeTagPopover = () => setTagPopover({ open: false, rect: null });

  const skipNextQuery = () => toast.info("Skipping the next scheduled query is coming soon.");

  if (loading || !debtor) return null;

  const j = debtor.judgment || {};

  return (
    <div className="space-y-6" data-testid="debtor-profile-page">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-[13px] text-text-secondary" data-testid="debtor-breadcrumbs">
        <button
          type="button"
          onClick={() => nav("/dashboard")}
          className="hover:text-accent-pink transition-colors"
          data-testid="breadcrumb-dashboard"
        >
          Dashboard
        </button>
        <ChevronRight size={12} className="text-border-default" />
        <button
          type="button"
          onClick={() => nav("/debtors")}
          className="hover:text-accent-pink transition-colors"
          data-testid="breadcrumb-debtors"
        >
          My Debtors
        </button>
        <ChevronRight size={12} className="text-border-default" />
        <span className="text-white" data-testid="breadcrumb-current">{debtor.full_name}</span>
      </nav>

      {/* Header card */}
      <div className="bg-bg-secondary border border-border-default rounded-[12px] p-8 flex items-start justify-between gap-6"
           data-testid="debtor-profile-header">
        <div className="min-w-0 space-y-3">
          <h1 className="text-white font-extrabold text-[22px]">{debtor.full_name}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <TierPill tier={debtor.tier} />
            <StatusPill status={debtor.status} />
            {debtor.status === "archived" && (
              <span
                className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.08em] uppercase text-white"
                style={{ background: "#5A7A9A" }}
                data-testid="header-archived-pill"
              >
                Archived
              </span>
            )}
          </div>
          {isPro && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="header-tags-row">
              {resolveTags(debtor.tag_ids, lookup).map((t) => (
                <TagPill key={t.id} tag={t} size="lg" />
              ))}
              <button
                type="button"
                onClick={openTagPopover}
                className="inline-flex items-center gap-1 text-[12px] text-text-secondary hover:text-accent-pink border border-dashed border-border-default rounded-[4px] px-2 py-[3px]"
                data-testid="header-tags-edit"
              >
                <TagIcon size={11} />
                {(debtor.tag_ids || []).length ? "Edit tags" : "Add tags"}
              </button>
            </div>
          )}
          <div>
            <div className="jea-caption">Judgment Amount</div>
            <div className="jea-mono text-accent-pink font-bold text-[18px]" data-testid="header-judgment-amount">
              {centsToUSD(j.amount_cents || 0)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {canRunQueries && (
            <JeaButton
              className="gap-2"
              onClick={() => setRunQueryOpen(true)}
              disabled={debtor.status === "archived"}
              data-testid="header-run-query"
            >
              <Play size={14} /> Run Query
            </JeaButton>
          )}
          {canMutateDebtors && (
            <JeaButton
              variant="outline-pink"
              className="gap-2"
              onClick={() => setEditOpen(true)}
              data-testid="header-edit-profile"
            >
              <Pencil size={14} /> Edit Profile
            </JeaButton>
          )}
          {canManageTeam && isPro && (
            <button
              ref={recipientsBtnRef}
              type="button"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setRecipientsPopover({ open: true, rect });
              }}
              className="h-11 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 transition-colors text-[14px] font-semibold flex items-center gap-2"
              data-testid="header-notification-recipients"
              title="Notification recipients"
            >
              <Bell size={14} /> Recipients
            </button>
          )}
          {canMutateDebtors && (debtor.status === "archived" ? (
            <JeaButton
              variant="outline-pink"
              className="gap-2"
              onClick={restore}
              data-testid="header-restore-debtor"
            >
              <ArchiveRestore size={14} /> Restore Judgment
            </JeaButton>
          ) : (
            <button
              type="button"
              onClick={archive}
              className="h-11 px-4 rounded-[8px] border border-danger text-danger hover:bg-danger hover:text-white transition-colors text-[14px] font-semibold flex items-center gap-2"
              data-testid="header-archive-debtor"
            >
              <ArchiveIcon size={14} /> Archive Judgment
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-bg-secondary border border-border-default rounded-[12px] p-1 flex gap-1" data-testid="profile-tabs">
        {TABS.map((t) => {
          const active = t.key === activeTab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "h-10 px-5 rounded-[8px] text-[14px] font-medium transition-all",
                active
                  ? "bg-accent-pink text-[#091424] font-bold"
                  : "text-text-secondary hover:bg-bg-tertiary hover:text-white"
              )}
              data-testid={`profile-tab-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="min-w-0 space-y-6">
        {activeTab === "overview" && (
          <>
            <OverviewTab
              debtor={debtor}
              onSkipQuery={canMutateDebtors ? skipNextQuery : null}
              onRunQuery={canRunQueries ? () => setRunQueryOpen(true) : null}
            />
            <CollectionEventsSection debtor={debtor} />
          </>
        )}
        {activeTab === "query-history" && <QueryHistoryTab debtorId={id} />}
        {activeTab === "reports" && <ReportsTab debtorId={id} />}
        {activeTab === "renewal" && <RenewalTab debtor={debtor} />}
        {activeTab === "documents" && <DocumentsTab debtorId={id} />}
        {activeTab === "notes" && <NotesTab debtorId={id} />}
      </div>

      <RunQueryModal
        open={runQueryOpen}
        debtor={debtor}
        onClose={() => setRunQueryOpen(false)}
        onCompleted={onRunCompleted}
      />

      <EditDebtorProfileModal
        open={editOpen}
        debtor={debtor}
        onClose={closeEdit}
        onSaved={onEditSaved}
      />

      {isPro && (
        <TagAssignPopover
          open={tagPopover.open}
          debtorId={id}
          initialTagIds={debtor.tag_ids || []}
          anchorRect={tagPopover.rect}
          onClose={closeTagPopover}
          onSaved={() => { closeTagPopover(); load(); }}
        />
      )}

      {canManageTeam && isPro && (
        <RecipientsPopover
          open={recipientsPopover.open}
          debtorId={id}
          anchorRect={recipientsPopover.rect}
          onClose={() => setRecipientsPopover({ open: false, rect: null })}
        />
      )}
    </div>
  );
};

export default DebtorProfilePage;
